/**
 * 🏭 useKioskMode — 2026-05-18
 *
 * Détecte si la session courante est en mode kiosque (Entrepôt partagé).
 * Détection : `?kiosk=1` dans l'URL OU `kiosk_mode === 'entrepot'` dans localStorage.
 *
 * Fournit :
 *   - isKiosk            : booléen
 *   - kioskEmployee      : objet employé connecté ({prenom, nom, nom_complet, photo_url, numero_employe})
 *   - kioskLogout()      : purge localStorage + redirection vers /kiosk/entrepot
 *   - remainingSeconds   : compte à rebours avant auto-logout (5 min d'inactivité)
 *
 * Auto-logout : 5 min d'inactivité (réinit sur mousemove/keydown/touchstart/click).
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const INACTIVITY_MS = 5 * 60 * 1000; // 5 minutes
const TICK_MS = 1000;

export function useKioskMode() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const kioskParam = params.get('kiosk') === '1';
  const kioskMarker = typeof window !== 'undefined' && localStorage.getItem('kiosk_mode') === 'entrepot';
  const isKiosk = kioskParam || kioskMarker;

  const [kioskEmployee, setKioskEmployee] = useState(() => {
    try {
      const raw = localStorage.getItem('kiosk_employee');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const lastActivityRef = useRef(Date.now());
  const [remainingSeconds, setRemainingSeconds] = useState(INACTIVITY_MS / 1000);

  const kioskLogout = useCallback((reason = 'manual') => {
    try {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      localStorage.removeItem('kiosk_mode');
      localStorage.removeItem('kiosk_employee');
      localStorage.removeItem('kiosk_started_at');
    } catch (e) {
      console.error('Error:', e);
    }
    // Hard redirect (purge le state React et l'AuthContext)
    window.location.replace(`/kiosk/entrepot?reason=${reason}`);
  }, []);

  // Timer d'inactivité
  useEffect(() => {
    if (!isKiosk) return undefined;

    const bump = () => { lastActivityRef.current = Date.now(); };
    // 🐛 2026-05-19 — Avant : `mousemove` et `scroll` étaient écoutés. En pratique,
    //   la souris bouge constamment (cursor drift, micro-mouvements quand on
    //   parle, etc.), donc le timer ne descendait JAMAIS et la déconnexion auto
    //   ne s'activait pas. Maintenant on écoute uniquement les actions
    //   délibérées (clic, tape clavier/scanner, touch). Si l'employé scanne ou
    //   clique, le timer se réinitialise. Sinon il décompte vraiment.
    const events = ['keydown', 'touchstart', 'click'];
    events.forEach((ev) => window.addEventListener(ev, bump, { passive: true }));

    const tick = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      const remaining = Math.max(0, Math.ceil((INACTIVITY_MS - elapsed) / 1000));
      setRemainingSeconds(remaining);
      if (elapsed >= INACTIVITY_MS) {
        clearInterval(tick);
        kioskLogout('inactivity');
      }
    }, TICK_MS);

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, bump));
      clearInterval(tick);
    };
  }, [isKiosk, kioskLogout]);

  // Re-sync employee si le marker apparaît plus tard
  useEffect(() => {
    if (!isKiosk) {
      setKioskEmployee(null);
      return;
    }
    try {
      const raw = localStorage.getItem('kiosk_employee');
      setKioskEmployee(raw ? JSON.parse(raw) : null);
    } catch {
      setKioskEmployee(null);
    }
  }, [isKiosk]);

  return { isKiosk, kioskEmployee, kioskLogout, remainingSeconds };
}
